import {
  CreateDropdown,
  createMenuItemClasses,
} from "@app/components/assistant/CreateDropdown";
import { CreatePodModal } from "@app/components/assistant/conversation/CreatePodModal";
import { InputBarContext } from "@app/components/assistant/conversation/input_bar/InputBarContext";
import { InviteEmailButtonWithModal } from "@app/components/members/InviteEmailButtonWithModal";
import type { NavigationPanelSection } from "@app/components/navigation/DesktopNavigationContext";
import { SidebarContext } from "@app/components/sparkle/SidebarContext";
import { useActiveConversationId } from "@app/hooks/useActiveConversationId";
import { isFreePlan } from "@app/lib/plans/plan_codes";
import { useAppRouter } from "@app/lib/platform";
import { usePerSeatPricing } from "@app/lib/swr/workspaces";
import { getConversationRoute, getPodRoute } from "@app/lib/utils/router";
import type { SubscriptionType } from "@app/types/plan";
import { assertNeverAndIgnore } from "@app/types/shared/utils/assert_never";
import type { WorkspaceType } from "@app/types/user";
import {
  cn,
  DropdownMenuItem,
  Icon,
  Plus,
  Tooltip,
  UserPlus01,
} from "@dust-tt/sparkle";
import type { ReactNode } from "react";
import { forwardRef, useContext, useState } from "react";

interface PanelCreateButtonProps {
  owner: WorkspaceType;
  subscription: SubscriptionType;
  section: NavigationPanelSection;
}

interface PlusButtonProps {
  label: string;
  onClick?: () => void;
}

const PlusButton = forwardRef<HTMLButtonElement, PlusButtonProps>(
  function PlusButton({ label, ...props }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        className={cn(
          "group flex h-8 w-8 items-center justify-center rounded-lg border-2 outline-none",
          "border-blue-300 text-blue-600",
          "dark:border-blue-400/70 dark:text-blue-100",
          "transition-[border-color,background-color,box-shadow,transform] duration-300 ease-out",
          "hover:-translate-y-px hover:border-blue-400 hover:bg-blue-100 hover:shadow-[0_4px_12px_-4px_rgb(79_147_189/0.45)]",
          "dark:hover:border-blue-300 dark:hover:bg-blue-500/25",
          "data-[state=open]:border-blue-400 data-[state=open]:bg-blue-100 dark:data-[state=open]:bg-blue-500/25",
          "active:translate-y-0 active:scale-95",
          "focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-2 focus-visible:ring-offset-app-background",
          "motion-reduce:transition-none motion-reduce:active:scale-100 motion-reduce:hover:translate-y-0"
        )}
        {...props}
      >
        <Icon
          visual={Plus}
          size="sm"
          aria-hidden
          className="transition-transform duration-300 ease-out group-hover:rotate-90 group-data-[state=open]:rotate-45 motion-reduce:transition-none motion-reduce:group-hover:rotate-0 motion-reduce:group-data-[state=open]:rotate-0"
        />
      </button>
    );
  }
);

interface InstantTooltipProps {
  label: string;
  children: ReactNode;
}

function InstantTooltip({ label, children }: InstantTooltipProps) {
  return (
    <Tooltip
      label={label}
      trigger={children}
      tooltipTriggerAsChild
      delayDuration={0}
    />
  );
}

export function PanelCreateButton({
  owner,
  subscription,
  section,
}: PanelCreateButtonProps) {
  const router = useAppRouter();
  const activeConversationId = useActiveConversationId();
  const { setSidebarOpen } = useContext(SidebarContext);
  const { setShouldFocusInput } = useContext(InputBarContext);
  const { perSeatPricing } = usePerSeatPricing({
    workspaceId: owner.sId,
    disabled: section !== "contacts",
  });
  const [isCreatePodModalOpen, setIsCreatePodModalOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  switch (section) {
    case "dms":
      return (
        <InstantTooltip label="New chat">
          <PlusButton
            label="New chat"
            onClick={() => {
              setSidebarOpen(false);
              const isNewConversation =
                router.pathname.match(/^\/w\/[^/]+\/conversation\/[^/]+$/) !==
                  null && activeConversationId === null;
              if (isNewConversation) {
                setShouldFocusInput(true);
                return;
              }
              void router.push(getConversationRoute(owner.sId));
            }}
          />
        </InstantTooltip>
      );
    case "projects":
      return (
        <>
          <InstantTooltip label="New project">
            <PlusButton
              label="New project"
              onClick={() => setIsCreatePodModalOpen(true)}
            />
          </InstantTooltip>
          <CreatePodModal
            isOpen={isCreatePodModalOpen}
            onClose={() => setIsCreatePodModalOpen(false)}
            onCreated={(pod) => {
              setSidebarOpen(false);
              void router.push(getPodRoute(owner.sId, pod.sId));
            }}
            owner={owner}
          />
        </>
      );
    case "contacts":
      return (
        <>
          <CreateDropdown
            owner={owner}
            dataGtmLocation="contactsPanel"
            openOnHover
            trigger={<PlusButton label="New contact, agent or skill" />}
          >
            <DropdownMenuItem
              label="New contact"
              icon={UserPlus01}
              className={createMenuItemClasses}
              onClick={() => setIsInviteOpen(true)}
            />
          </CreateDropdown>
          <InviteEmailButtonWithModal
            owner={owner}
            prefillText=""
            perSeatPricing={perSeatPricing}
            isFreePlan={isFreePlan(subscription.plan.code)}
            open={isInviteOpen}
            onOpenChange={setIsInviteOpen}
          />
        </>
      );
    default:
      assertNeverAndIgnore(section);
      return null;
  }
}
